import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Alert,
  Checkbox,
  Grid,
} from 'antd';
import dayjs from 'dayjs';
import { employeeService } from '../../services/employeeService';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const MobileDateRange = ({
  value,
  onChange,
  disabled,
  disabledDate,
  format,
  startPlaceholder,
  endPlaceholder,
}) => {
  const [start, end] = value || [null, null];

  const emit = (next) => onChange?.(next);

  const handleStartChange = (newStart) => {
    if (newStart && end && newStart.isAfter(end, 'day')) {
      emit([newStart, null]);
      return;
    }
    emit([newStart, end || null]);
  };

  const handleEndChange = (newEnd) => {
    emit([start || null, newEnd]);
  };

  const endDisabledDate = (current) => {
    if (disabledDate && disabledDate(current)) return true;
    if (start && current && current.isBefore(start, 'day')) return true;
    return false;
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <DatePicker
        value={start}
        onChange={handleStartChange}
        style={{ width: '100%' }}
        disabled={disabled}
        disabledDate={disabledDate}
        format={format}
        placeholder={startPlaceholder}
        inputReadOnly
        data-testid="mobile-date-start"
      />
      <DatePicker
        value={end}
        onChange={handleEndChange}
        style={{ width: '100%' }}
        disabled={disabled}
        disabledDate={endDisabledDate}
        format={format}
        placeholder={endPlaceholder}
        inputReadOnly
        data-testid="mobile-date-end"
      />
    </Space>
  );
};

const LeaveRequestForm = ({ open, onClose, onSubmit, selectedDate = null, loading = false, editingRequest = null, bankHolidays = new Set() }) => {
  const [form] = Form.useForm();
  const watchedDates = Form.useWatch('dates', form);
  const [employees, setEmployees] = useState([]);
  const [vacationAvailability, setVacationAvailability] = useState(null);
  const prevFetchParamsRef = useRef({});
  const { user, canManageEmployees } = useAuth();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isEditMode = editingRequest != null;
  const isAdminOrHr =
    canManageEmployees?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));
  const canSelectEmployee = !isEditMode && isAdminOrHr;
  const canOverrideAccrual = isAdminOrHr;

  const holidayConflicts = useMemo(() => {
    if (!watchedDates || !watchedDates[0] || !watchedDates[1] || bankHolidays.size === 0) return [];
    const conflicts = [];
    let cursor = watchedDates[0].startOf('day');
    const end = watchedDates[1].startOf('day');
    while (!cursor.isAfter(end, 'day')) {
      const iso = cursor.format('YYYY-MM-DD');
      if (bankHolidays.has(iso)) conflicts.push(iso);
      cursor = cursor.add(1, 'day');
    }
    return conflicts;
  }, [watchedDates, bankHolidays]);

  const leaveTypes = [
    { value: 'VACATION', label: t('leave.form.types.VACATION') },
    { value: 'SICK', label: t('leave.form.types.SICK') },
    { value: 'MATERNITY', label: t('leave.form.types.MATERNITY') },
    { value: 'PATERNITY', label: t('leave.form.types.PATERNITY') },
    { value: 'BEREAVEMENT', label: t('leave.form.types.BEREAVEMENT') },
    { value: 'MARRIAGE', label: t('leave.form.types.MARRIAGE') },
  ];

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        form.setFieldsValue({
          employeeId: editingRequest.employeeId,
          dates: [dayjs(editingRequest.startDate), dayjs(editingRequest.endDate)],
          leaveType: editingRequest.leaveType,
          reason: editingRequest.reason || '',
        });
      } else {
        if (canSelectEmployee) {
          fetchEmployees();
        } else {
          setEmployees([]);
          form.setFieldValue('employeeId', user?.employeeId);
        }

        if (selectedDate) {
          const dateValue = dayjs(selectedDate);
          form.setFieldValue('dates', [dateValue, dateValue]);
          // Eagerly fetch when opening with a pre-selected date — setFieldValue does
          // not always trigger onFieldsChange reliably in all rendering environments.
          const employeeId = canSelectEmployee ? form.getFieldValue('employeeId') : user?.employeeId;
          const leaveType = form.getFieldValue('leaveType') || 'VACATION';
          prevFetchParamsRef.current = {
            employeeId,
            leaveType,
            datesKey: `${dateValue.valueOf()}-${dateValue.valueOf()}`,
          };
          fetchVacationAvailability(employeeId, [dateValue, dateValue], leaveType);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate, editingRequest, form, canSelectEmployee, user?.employeeId, isEditMode]);

  const fetchEmployees = async () => {
    if (!canSelectEmployee) {
      return;
    }

    try {
      const data = await employeeService.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchVacationAvailability = async (employeeId, dates, leaveType) => {
    if (!employeeId || !dates || !dates[0] || !dates[1] || leaveType !== 'VACATION') {
      setVacationAvailability(null);
      return;
    }

    try {
      // Use the END date's month — the cap accrued by the end of the request's month
      const month = dates[1].month() + 1;
      const year = dates[1].year();
      const data = await leaveService.getVacationAvailability(employeeId, month, year);
      setVacationAvailability(data);
    } catch (error) {
      console.error('Error fetching vacation availability:', error);
      setVacationAvailability(null);
    }
  };

  const handleFieldsChange = () => {
    const employeeId = canSelectEmployee ? form.getFieldValue('employeeId') : user?.employeeId;
    const dates = form.getFieldValue('dates');
    const leaveType = form.getFieldValue('leaveType');

    const datesKey = dates && dates[0] && dates[1]
      ? `${dates[0].valueOf()}-${dates[1].valueOf()}`
      : null;
    const prev = prevFetchParamsRef.current;
    if (prev.employeeId === employeeId && prev.leaveType === leaveType && prev.datesKey === datesKey) {
      return;
    }
    prevFetchParamsRef.current = { employeeId, leaveType, datesKey };
    fetchVacationAvailability(employeeId, dates, leaveType);
  };

  const handleFinish = (values) => {
    // In edit mode the employee can't be changed; always carry the request's
    // existing employeeId — admins may have user.employeeId === null.
    const employeeId = isEditMode
      ? editingRequest.employeeId
      : (canSelectEmployee ? values.employeeId : user?.employeeId);
    const formData = {
      employeeId,
      startDate: values.dates[0].format('YYYY-MM-DD'),
      endDate: values.dates[1].format('YYYY-MM-DD'),
      leaveType: values.leaveType,
      reason: values.reason || '',
      overrideAccrual: canOverrideAccrual && Boolean(values.overrideAccrual),
    };
    onSubmit(formData);
  };

  const handleClose = () => {
    form.resetFields();
    setVacationAvailability(null);
    prevFetchParamsRef.current = {};
    onClose();
  };

  const disabledDate = (current) => {
    if (isEditMode) return false;
    if (current && current < dayjs().startOf('day')) return true;
    if (current && (current.day() === 0 || current.day() === 6)) return true;
    return false;
  };

  return (
    <Modal
      title={isEditMode ? t('leave.form.editTitle') : t('leave.form.title')}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          {isEditMode ? t('leave.form.update') : t('leave.form.submit')}
        </Button>,
      ]}
      width={isMobile ? '95vw' : 600}
      style={isMobile ? { top: 16 } : undefined}
      >
        <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFieldsChange={handleFieldsChange}
        initialValues={{
          leaveType: 'VACATION',
          overrideAccrual: false,
        }}
      >
        {canSelectEmployee && (
          <Form.Item
            name="employeeId"
            label={t('leave.form.employee')}
            rules={[{ required: true, message: t('leave.form.employeeRequired') }]}
          >
            <Select
              placeholder={t('leave.form.employeePlaceholder')}
              disabled={loading || !!editingRequest}
              showSearch
              optionFilterProp="label"
              notFoundContent={t('leave.form.employeeNotFound')}
            >
              {employees.map((employee) => (
                <Option
                  key={employee.id}
                  value={employee.id}
                  label={`${employee.firstName} ${employee.lastName} - ${employee.departmentName}`}
                >
                  <Space>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: employee.departmentColor,
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                    {employee.firstName} {employee.lastName} - {employee.departmentName}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="dates"
          label={t('leave.form.dates')}
          rules={[
            { required: true, message: t('leave.form.datesRequired') },
            {
              validator: (_, value) =>
                value && value[0] && value[1]
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('leave.form.datesRequired'))),
            },
          ]}
        >
          {isMobile ? (
            <MobileDateRange
              disabled={loading}
              disabledDate={disabledDate}
              format="YYYY-MM-DD"
              startPlaceholder={t('leave.form.startDate')}
              endPlaceholder={t('leave.form.endDate')}
            />
          ) : (
            <RangePicker
              style={{ width: '100%' }}
              disabled={loading}
              disabledDate={disabledDate}
              format="YYYY-MM-DD"
            />
          )}
        </Form.Item>

        {isAdminOrHr && holidayConflicts.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="holiday-overlap-warning"
            message={t('leave.form.holidayOverlapWarning', { dates: holidayConflicts.join(', ') })}
          />
        )}

        <Form.Item
          name="leaveType"
          label={t('leave.form.type')}
          rules={[{ required: true, message: t('leave.form.typeRequired') }]}
        >
          <Select placeholder={t('leave.form.typePlaceholder')} disabled={loading}>
            {leaveTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {vacationAvailability && !vacationAvailability.collaborator && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="vacation-availability"
            message={t('leave.form.vacationAvailability', {
              month: vacationAvailability.month,
              year: vacationAvailability.year,
            })}
            description={
              <div>
                <div>
                  {t('leave.form.accruedDays')}:{' '}
                  <strong data-testid="availability-accrued">
                    {vacationAvailability.availableFromCurrentYear}
                  </strong>
                </div>
                <div>
                  {t('leave.form.carriedOverDays')}:{' '}
                  <strong data-testid="availability-carry-over">
                    {vacationAvailability.carriedOverRemaining}
                  </strong>
                </div>
                <div>
                  {t('leave.form.totalAvailable')}:{' '}
                  <strong data-testid="availability-total">
                    {vacationAvailability.totalAvailable}
                  </strong>
                </div>
              </div>
            }
          />
        )}

        {vacationAvailability && vacationAvailability.collaborator && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('leave.form.collaboratorInfo')}
          />
        )}

        {canOverrideAccrual && (
          <Form.Item
            name="overrideAccrual"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Checkbox data-testid="override-accrual-checkbox">
              {t('leave.form.overrideAccrual')}
            </Checkbox>
          </Form.Item>
        )}

        <Form.Item
          name="reason"
          label={t('leave.form.reason')}
        >
          <TextArea
            rows={4}
            placeholder={t('leave.form.reasonPlaceholder')}
            disabled={loading}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LeaveRequestForm;
